import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-routing-machine';
import 'leaflet-routing-machine/dist/leaflet-routing-machine.css';
import { X, MapPin, Store, Navigation, Clock, Route, Loader2, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const pickupIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
    width: 36px;
    height: 36px;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid white;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  ">
    <svg style="transform: rotate(45deg); width: 18px; height: 18px; color: white;" fill="currentColor" viewBox="0 0 24 24">
      <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4"/>
    </svg>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
});

const deliveryIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    width: 36px;
    height: 36px;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid white;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  ">
    <svg style="transform: rotate(45deg); width: 18px; height: 18px; color: white;" fill="currentColor" viewBox="0 0 24 24">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
});

// Routing component
const RoutingMachine = ({ pickupCoords, deliveryCoords, onRouteFound }) => {
  const map = useMap();
  const routingControlRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!map || !pickupCoords || !deliveryCoords) return;

    // Small delay to ensure map is fully ready
    const initTimeout = setTimeout(() => {
      try {
        // Remove existing routing control safely
        if (routingControlRef.current && map.hasLayer && routingControlRef.current._container) {
          try {
            map.removeControl(routingControlRef.current);
          } catch (e) {
            console.log('Could not remove previous routing control');
          }
        }

        // Create routing control
        const routingControl = L.Routing.control({
          waypoints: [
            L.latLng(pickupCoords[0], pickupCoords[1]),
            L.latLng(deliveryCoords[0], deliveryCoords[1])
          ],
          routeWhileDragging: false,
          addWaypoints: false,
          draggableWaypoints: false,
          fitSelectedRoutes: true,
          showAlternatives: false,
          createMarker: () => null, // We use our own markers
          lineOptions: {
            styles: [
              { color: '#E31E24', opacity: 0.8, weight: 6 },
              { color: '#FF6B35', opacity: 0.5, weight: 10 }
            ],
            extendToWaypoints: true,
            missingRouteTolerance: 0
          },
          router: L.Routing.osrmv1({
            serviceUrl: 'https://router.project-osrm.org/route/v1',
            profile: 'driving'
          })
        });

        routingControl.on('routesfound', (e) => {
          const routes = e.routes;
          if (routes && routes[0]) {
            const summary = routes[0].summary;
            onRouteFound({
              distance: (summary.totalDistance / 1000).toFixed(1), // km
              duration: Math.round(summary.totalTime / 60) // minutes
            });
          }
        });

        routingControl.on('routingerror', (e) => {
          console.error('Routing error:', e);
          // Calculate approximate distance/time based on straight line
          const distance = map.distance(
            L.latLng(pickupCoords[0], pickupCoords[1]),
            L.latLng(deliveryCoords[0], deliveryCoords[1])
          );
          onRouteFound({ 
            distance: (distance / 1000).toFixed(1), 
            duration: Math.round((distance / 1000) * 3), // Approximate 3 min per km
            approximate: true 
          });
        });

        routingControl.addTo(map);
        routingControlRef.current = routingControl;
        setIsInitialized(true);

        // Hide the routing instructions panel
        setTimeout(() => {
          const container = document.querySelector('.leaflet-routing-container');
          if (container) {
            container.style.display = 'none';
          }
        }, 100);
      } catch (error) {
        console.error('Error initializing routing:', error);
        // Fallback: just draw a line between points
        const distance = map.distance(
          L.latLng(pickupCoords[0], pickupCoords[1]),
          L.latLng(deliveryCoords[0], deliveryCoords[1])
        );
        onRouteFound({ 
          distance: (distance / 1000).toFixed(1), 
          duration: Math.round((distance / 1000) * 3),
          approximate: true 
        });
      }
    }, 500);

    return () => {
      clearTimeout(initTimeout);
      if (routingControlRef.current && map) {
        try {
          map.removeControl(routingControlRef.current);
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [map, pickupCoords, deliveryCoords, onRouteFound]);

  return null;
};

// Geocoding function using Nominatim (OpenStreetMap)
const geocodeAddress = async (address, city = 'Luanda, Angola') => {
  try {
    const query = `${address}, ${city}`;
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`
    );
    const data = await response.json();
    if (data && data[0]) {
      return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
    }
    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
};

const DeliveryMapModal = ({ isOpen, onClose, order }) => {
  const [pickupCoords, setPickupCoords] = useState(null);
  const [deliveryCoords, setDeliveryCoords] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !order) return;

    const loadCoordinates = async () => {
      setLoading(true);
      setError(null);
      setRouteInfo(null);

      try {
        // Default Luanda coordinates as fallback
        const defaultLuanda = [-8.8390, 13.2894];
        
        // Try to get pickup station coordinates
        let pickup = null;
        if (order.pickup_station_address) {
          pickup = await geocodeAddress(order.pickup_station_address);
        }
        // Use station name as fallback
        if (!pickup && order.pickup_station_name) {
          pickup = await geocodeAddress(order.pickup_station_name);
        }
        // Default to known Luanda locations based on station name
        if (!pickup) {
          if (order.pickup_station_name?.includes('Mutamba')) {
            pickup = [-8.8147, 13.2302]; // Largo da Mutamba
          } else if (order.pickup_station_name?.includes('Maianga')) {
            pickup = [-8.8283, 13.2344]; // Maianga
          } else if (order.pickup_station_name?.includes('Viana')) {
            pickup = [-8.9069, 13.3728]; // Viana
          } else {
            pickup = defaultLuanda;
          }
        }

        // Try to get delivery address coordinates
        let delivery = null;
        const deliveryAddress = order.delivery_address || order.address;
        if (deliveryAddress) {
          delivery = await geocodeAddress(deliveryAddress);
        }
        // Generate a random offset from pickup if geocoding fails
        if (!delivery) {
          const offsetLat = (Math.random() - 0.5) * 0.05;
          const offsetLon = (Math.random() - 0.5) * 0.05;
          delivery = [pickup[0] + offsetLat, pickup[1] + offsetLon];
        }

        setPickupCoords(pickup);
        setDeliveryCoords(delivery);
      } catch (err) {
        console.error('Error loading coordinates:', err);
        setError('Erro ao carregar mapa');
      }
      
      setLoading(false);
    };

    loadCoordinates();
  }, [isOpen, order]);

  if (!isOpen) return null;

  const center = pickupCoords || [-8.8390, 13.2894]; // Default to Luanda center

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-zinc-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-zinc-700 shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-sonangol-orange to-orange-600 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Navigation className="w-5 h-5 text-white" />
            <h2 className="font-barlow font-bold text-white text-lg">Mapa da Entrega</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Order info */}
        <div className="px-4 py-3 bg-zinc-900/50 border-b border-zinc-700">
          <div className="flex items-center justify-between">
            <span className="font-barlow font-bold text-white">
              Pedido #{order?.id?.slice(0, 8)}
            </span>
            <span className="font-barlow font-bold text-green-500">
              {order?.total_aoa?.toLocaleString('pt-AO')} AOA
            </span>
          </div>
        </div>

        {/* Route info cards */}
        <div className="px-4 py-3 grid grid-cols-2 gap-3">
          {/* Pickup */}
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
            <div className="flex items-center gap-2 text-green-400 text-xs font-medium mb-1">
              <Store className="w-4 h-4" />
              <span>RECOLHA</span>
            </div>
            <p className="text-white text-sm font-medium truncate">
              {order?.pickup_station_name || order?.station_name || 'Estação'}
            </p>
          </div>
          
          {/* Delivery */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
            <div className="flex items-center gap-2 text-blue-400 text-xs font-medium mb-1">
              <MapPin className="w-4 h-4" />
              <span>ENTREGA</span>
            </div>
            <p className="text-white text-sm font-medium truncate">
              {order?.delivery_address || order?.address || 'Endereço'}
            </p>
          </div>
        </div>

        {/* Route estimation */}
        {routeInfo && !routeInfo.error && (
          <div className="px-4 pb-3">
            <div className="bg-sonangol-orange/10 border border-sonangol-orange/30 rounded-lg p-3 flex items-center justify-around">
              <div className="flex items-center gap-2">
                <Route className="w-5 h-5 text-sonangol-orange" />
                <div>
                  <p className="text-zinc-400 text-xs">Distância</p>
                  <p className="text-white font-bold">{routeInfo.distance} km</p>
                </div>
              </div>
              <div className="w-px h-8 bg-zinc-700" />
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-sonangol-orange" />
                <div>
                  <p className="text-zinc-400 text-xs">Tempo Est.</p>
                  <p className="text-white font-bold">{routeInfo.duration} min</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Map container */}
        <div className="relative h-[400px] bg-zinc-900">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="w-10 h-10 text-sonangol-orange animate-spin mx-auto mb-2" />
                <p className="text-zinc-400 text-sm">A carregar mapa...</p>
              </div>
            </div>
          ) : error ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <MapPin className="w-10 h-10 text-red-500 mx-auto mb-2" />
                <p className="text-red-400">{error}</p>
              </div>
            </div>
          ) : (
            <MapContainer
              center={center}
              zoom={13}
              style={{ height: '100%', width: '100%' }}
              zoomControl={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {pickupCoords && (
                <Marker position={pickupCoords} icon={pickupIcon}>
                  <Popup>
                    <div className="text-center">
                      <p className="font-bold text-green-600">RECOLHA</p>
                      <p className="text-sm">{order?.pickup_station_name || 'Estação'}</p>
                    </div>
                  </Popup>
                </Marker>
              )}
              
              {deliveryCoords && (
                <Marker position={deliveryCoords} icon={deliveryIcon}>
                  <Popup>
                    <div className="text-center">
                      <p className="font-bold text-blue-600">ENTREGA</p>
                      <p className="text-sm">{order?.delivery_address || order?.address}</p>
                    </div>
                  </Popup>
                </Marker>
              )}
              
              {pickupCoords && deliveryCoords && (
                <RoutingMachine
                  pickupCoords={pickupCoords}
                  deliveryCoords={deliveryCoords}
                  onRouteFound={setRouteInfo}
                />
              )}
            </MapContainer>
          )}
        </div>

        {/* Legend */}
        <div className="px-4 py-3 bg-zinc-900/50 border-t border-zinc-700 flex items-center justify-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-500" />
            <span className="text-zinc-400 text-xs">Recolha</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-blue-500" />
            <span className="text-zinc-400 text-xs">Entrega</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-1 rounded bg-sonangol-orange" />
            <span className="text-zinc-400 text-xs">Rota</span>
          </div>
        </div>

        {/* Navigation Apps */}
        {pickupCoords && deliveryCoords && (
          <div className="px-4 py-3 border-t border-zinc-700">
            <p className="text-zinc-400 text-xs text-center mb-3">Abrir navegação externa:</p>
            <div className="grid grid-cols-2 gap-3">
              {/* Google Maps Button */}
              <a
                href={`https://www.google.com/maps/dir/?api=1&origin=${pickupCoords[0]},${pickupCoords[1]}&destination=${deliveryCoords[0]},${deliveryCoords[1]}&travelmode=driving`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-white hover:bg-gray-100 text-gray-800 rounded-xl font-bold transition-all shadow-lg"
                data-testid="open-google-maps-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335"/>
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 1.74.5 3.37 1.41 4.84L12 22l5.59-8.16C18.5 12.37 19 10.74 19 9c0-3.87-3.13-7-7-7z" fill="#4285F4" opacity="0.3"/>
                  <circle cx="12" cy="9" r="2.5" fill="#fff"/>
                </svg>
                <span className="text-sm">Google Maps</span>
                <ExternalLink className="w-3 h-3 opacity-50" />
              </a>
              
              {/* Waze Button */}
              <a
                href={`https://waze.com/ul?ll=${deliveryCoords[0]},${deliveryCoords[1]}&navigate=yes&from=${pickupCoords[0]},${pickupCoords[1]}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-[#33CCFF] hover:bg-[#29b8e8] text-white rounded-xl font-bold transition-all shadow-lg"
                data-testid="open-waze-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c3.86 0 7 3.14 7 7 0 3.86-3.14 7-7 7s-7-3.14-7-7c0-3.86 3.14-7 7-7zm-2.5 4c-.83 0-1.5.67-1.5 1.5S8.67 12 9.5 12s1.5-.67 1.5-1.5S10.33 9 9.5 9zm5 0c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm-5.8 4.4c-.3.5-.1 1.1.4 1.4.5.3 1.1.1 1.4-.4.7-1.1 1.9-1.4 3-1.4s2.3.3 3 1.4c.2.3.5.5.9.5.2 0 .4-.1.5-.1.5-.3.7-.9.4-1.4-1-1.7-2.8-2.4-4.8-2.4s-3.8.7-4.8 2.4z"/>
                </svg>
                <span className="text-sm">Waze</span>
                <ExternalLink className="w-3 h-3 opacity-70" />
              </a>
            </div>
            
            {/* Quick navigation shortcuts */}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${pickupCoords[0]},${pickupCoords[1]}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1 px-3 py-2 bg-green-500/20 text-green-400 rounded-lg text-xs hover:bg-green-500/30 transition-colors"
              >
                <Store className="w-3 h-3" />
                <span>Ir à Estação</span>
              </a>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${deliveryCoords[0]},${deliveryCoords[1]}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1 px-3 py-2 bg-blue-500/20 text-blue-400 rounded-lg text-xs hover:bg-blue-500/30 transition-colors"
              >
                <MapPin className="w-3 h-3" />
                <span>Ir à Entrega</span>
              </a>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="px-4 py-3 border-t border-zinc-700">
          <Button
            onClick={onClose}
            className="w-full bg-zinc-700 hover:bg-zinc-600 font-barlow font-bold"
          >
            Fechar Mapa
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryMapModal;
